import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { Provider, useAtom } from 'jotai/react'
import { useEffect } from 'react'

import { ContentLayout } from '@/components/layouts'
import { useLogout } from '@/features/auth/hooks'
import { findTenantDcId, getDefaultUserMeTenant } from '@/features/auth/models'
import { ConfigProvider } from '@/features/config/providers'
import { useAvailableDcs } from '@/features/dcs/hooks'
import { setSentryDcId, setSentryTenantKey } from '@/libs/sentry/sentry-config'
import {
  selectedDcIdAtom,
  tenantStore
} from '@/routes/_protected/$tenantKey/-states'
import { queryParamHistoryAtom } from '@/routes/_protected/$tenantKey/-states/atoms/query-param-history.atom'

import {
  Header,
  PackageCategoryImportModal,
  RightContentWrapper,
  ShipmentOrderCsvImportModal,
  ShipmentOrderImportModal,
  Sider,
  SiteImportModal,
  TariffImportModal,
  TourCsvImportModal,
  TruckClassImportModal,
  TruckImportModal
} from './-components'
import { useCurrentTenant } from './-hooks'

export const Route = createFileRoute('/_protected/$tenantKey')({
  component: TenantKeyRouteLayout,
  beforeLoad: ({ context, params: { tenantKey }, location, search }) => {
    // ユーザーが所属するテナントのキー以外の場合は、デフォルトテナントのキーにリダイレクト
    const { user } = context.auth
    const tenantKeys = user.tenants.map(({ key }) => key)

    if (!tenantKeys.includes(tenantKey)) {
      throw redirect({
        to: `/${getDefaultUserMeTenant(user).key}`
      })
    }

    // Sentry のイベントをテナント単位で切り分けられるようにする (#5134)。
    // ここは所属テナントの検証を通った後なので、記録するキーは必ず実在する。
    setSentryTenantKey(tenantKey)

    // 拠点も同じ場所で書く。レイアウトは useAvailableDcs (useSuspenseQuery) で
    // suspend するため、下の useEffect が走るのは拠点一覧が解決した後になる。
    // その窓で出るイベント (子ルートの loader 例外、X-DC-Id 未設定エラー、
    // pageload transaction) を取りこぼさないよう、ここでも書いておく。
    // ただし selectedDcIdAtom はテナント別ではなく、既定拠点も先頭テナント由来
    // (getDefaultDcId) なので、このテナントで有効と確認できた場合だけ記録する。
    // 確認できないものは null にして、拠点一覧が解決した後の useEffect に委ねる。
    setSentryDcId(
      findTenantDcId(user, tenantKey, tenantStore.get(selectedDcIdAtom))
    )

    if (Object.keys(search).length > 0) {
      // パンくずリストからのリンク時に検索条件を維持するためクエリパラメータの内容を保持しておく
      // ※ 1つルート階層内にクエリパラメータを使用するページが複数ある場合は改良が必要
      tenantStore.set(queryParamHistoryAtom, {
        path: location.pathname,
        params: search
      })
    }
  },
  loader: ({ context: { auth } }) => {
    return {
      authUser: auth.user
    }
  }
})

function TenantKeyRouteLayout() {
  const { authUser } = Route.useLoaderData()
  const currentTenant = useCurrentTenant()
  const logout = useLogout()
  const { tenantKey } = Route.useParams()

  const [selectedDcId, setSelectedDcId] = useAtom(selectedDcIdAtom, {
    store: tenantStore
  })
  const { data: dcs } = useAvailableDcs(tenantKey, authUser)
  // ユーザ切り替えや拠点削除により選択中の拠点が対象から外れた場合は先頭の拠点を設定する
  useEffect(() => {
    setSelectedDcId(
      selectedDcId && dcs.some((x) => x.id === selectedDcId)
        ? selectedDcId
        : dcs[0]?.id
    )
  }, [selectedDcId, setSelectedDcId, dcs])

  // Sentry のイベントから拠点を追えるようにする (#5134)。
  // 拠点はルートパラメータではなく選択状態なので、beforeLoad ではなくここで拾う。
  useEffect(() => {
    setSentryDcId(selectedDcId ?? null)
  }, [selectedDcId])

  return (
    <ConfigProvider tenantKey={tenantKey}>
      <Provider store={tenantStore}>
        <Sider authUser={authUser} currentTenant={currentTenant} />
        <ContentLayout
          className="flex flex-col bg-white"
          data-testid="tenant-key-route-layout"
        >
          <Header
            authUser={authUser}
            currentTenant={currentTenant}
            onLogout={logout}
            tenantKey={tenantKey}
          />
          <Outlet />
        </ContentLayout>
        <RightContentWrapper />
        <TourCsvImportModal tenantKey={tenantKey} />
        <ShipmentOrderImportModal tenantKey={tenantKey} />
        <ShipmentOrderCsvImportModal
          tenantKey={tenantKey}
          authUser={authUser}
        />
        <SiteImportModal tenantKey={tenantKey} />
        <PackageCategoryImportModal tenantKey={tenantKey} />
        <TariffImportModal tenantKey={tenantKey} />
        <TruckImportModal tenantKey={tenantKey} />
        <TruckClassImportModal tenantKey={tenantKey} />
      </Provider>
    </ConfigProvider>
  )
}
